import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  Box,
  Toolbar,
  Tooltip,
  Typography,
  ToggleButton,
  useTheme,
} from "@mui/material";
import JsonView from "@uiw/react-json-view";
import { appFonts } from "../theme";
import { vscodeTheme } from "@uiw/react-json-view/vscode";
import { lightTheme } from "@uiw/react-json-view/light";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import DownloadIcon from '@mui/icons-material/Download';
import KlvWindowPortal from "./KlvWindowPortal";
import { toMisb0601DetailedJson } from "@impleotv/libmisb-web";
import { sortKlvForDisplay } from "../klvDetailedSort.mjs";


interface RawKlvPacket {
  time: Date;
  klv: Record<string, unknown>;
}

interface KlvWindowProps {
  open: boolean;
  onClose: () => void;
  rawKlvPacket: RawKlvPacket | null;
}

const KLV_DETAILED_STORAGE_KEY = "klvWindow.detailed";
const KLV_TIME_ISO8601_STORAGE_KEY = "klvWindow.timeISO8601";

const KlvWindow = ({ open, onClose, rawKlvPacket }: KlvWindowProps) => {
  const theme = useTheme();
  const [isPaused, setIsPaused] = useState(false);
  const [portalReady, setPortalReady] = useState(false);
  const latestPacketRef = useRef<RawKlvPacket | null>(null);
  const [displayPacket, setDisplayPacket] = useState<RawKlvPacket | null>(null);
  const portalReadyTimerRef = useRef<number | null>(null);
  const isFirefox = typeof navigator !== "undefined" && /firefox/i.test(navigator.userAgent);

  const [detailed, setDetailed] = useState(() => {
    if (typeof window === "undefined") {
      return false;
    }
    const stored = window.localStorage.getItem(KLV_DETAILED_STORAGE_KEY);
    return stored === "true";
  });

  const [timeISO8601, setTimeISO8601] = useState(() => {
    if (typeof window === "undefined") {
      return false;
    }
    const stored = window.localStorage.getItem(KLV_TIME_ISO8601_STORAGE_KEY);
    return stored === null ? false : stored === "true";
  });

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    window.localStorage.setItem(KLV_DETAILED_STORAGE_KEY, String(detailed));
  }, [detailed]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    window.localStorage.setItem(KLV_TIME_ISO8601_STORAGE_KEY, String(timeISO8601));
  }, [timeISO8601]);

  useEffect(() => {
    if (!open) {
      if (typeof window !== "undefined" && portalReadyTimerRef.current !== null) {
        window.clearTimeout(portalReadyTimerRef.current);
        portalReadyTimerRef.current = null;
      }
      setPortalReady(false);
      latestPacketRef.current = null;
      setDisplayPacket(null);
    }
  }, [open]);

  useEffect(() => {
    return () => {
      if (typeof window !== "undefined" && portalReadyTimerRef.current !== null) {
        window.clearTimeout(portalReadyTimerRef.current);
        portalReadyTimerRef.current = null;
      }
    };
  }, []);

  const handlePortalReady = useCallback(() => {
    if (isFirefox && typeof window !== "undefined") {
      if (portalReadyTimerRef.current !== null) {
        window.clearTimeout(portalReadyTimerRef.current);
      }

      portalReadyTimerRef.current = window.setTimeout(() => {
        setPortalReady(true);
        portalReadyTimerRef.current = null;
      }, 120);
      return;
    }

    setPortalReady(true);
  }, [isFirefox]);

  // Update display data only when not paused
  useEffect(() => {
    if (!rawKlvPacket) {
      return;
    }

    latestPacketRef.current = rawKlvPacket;

    if (!portalReady || isPaused) {
      return;
    }

    setDisplayPacket(rawKlvPacket);
  }, [isPaused, rawKlvPacket, portalReady]);

  useEffect(() => {
    if (isPaused || !portalReady || !latestPacketRef.current) {
      return;
    }

    setDisplayPacket(latestPacketRef.current);
  }, [isPaused, portalReady]);

  const displayKlvPacket = displayPacket;

  const jsonViewTheme = {
    ...(theme.palette.mode === "dark" ? vscodeTheme : lightTheme),
    "--w-rjv-font-family": appFonts.mono,
    "--w-rjv-color": theme.palette.text.primary,
    "--w-rjv-background-color": theme.palette.background.paper,
    "--w-rjv-key-string": theme.palette.primary.main,
    "--w-rjv-line-color": theme.palette.divider,
    "--w-rjv-arrow-color": theme.palette.text.secondary,
    "--w-rjv-info-color": theme.palette.text.secondary,
    "--w-rjv-curlybraces-color": theme.palette.text.secondary,
    "--w-rjv-colon-color": theme.palette.text.secondary,
    "--w-rjv-brackets-color": theme.palette.text.secondary,
    "--w-rjv-type-string-color": theme.palette.warning.main,
    "--w-rjv-type-int-color": theme.palette.success.main,
    "--w-rjv-type-float-color": theme.palette.success.main,
    "--w-rjv-type-bigint-color": theme.palette.success.main,
    "--w-rjv-type-boolean-color": theme.palette.info.main,
    "--w-rjv-type-date-color": theme.palette.secondary.main,
    "--w-rjv-type-url-color": theme.palette.primary.main,
    "--w-rjv-type-null-color": theme.palette.secondary.main,
    "--w-rjv-type-nan-color": theme.palette.error.main,
    "--w-rjv-type-undefined-color": theme.palette.text.secondary,
    "--w-rjv-update-color": theme.palette.primary.main,
    "--w-rjv-copied-color": theme.palette.primary.main,
    "--w-rjv-copied-success-color": theme.palette.success.main,
  };
  
  const formatTimestamp = (date: Date): string => {
    if (timeISO8601) {
      return date.toISOString();
    }
    // Local time with month name: "2025 October 14, 13:06:34.769"
    const year = date.getFullYear();
    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    const month = monthNames[date.getMonth()];
    const day = date.getDate();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    const ms = String(date.getMilliseconds()).padStart(3, '0');
    return `${year} ${month} ${day}, ${hours}:${minutes}:${seconds}.${ms}`;
  };
  
  const timestampText = displayKlvPacket?.time ? formatTimestamp(displayKlvPacket.time) : null;
  
  const displayedKlv = displayKlvPacket?.klv;
  const klvData = useMemo(() => {
    const rawData = displayedKlv ?? {};
    if (!detailed) {
      return rawData;
    }
    try {
      return sortKlvForDisplay(toMisb0601DetailedJson(rawData));
    } catch (error) {
      console.error('Failed to convert KLV data to detailed format:', error);
      return rawData;
    }
  }, [detailed, displayedKlv]);
  
  const handleDownload = () => {
    if (!displayKlvPacket?.klv || !klvData) return;
    
    // Create filename from timestamp
    const timestamp = displayKlvPacket.time;
    const year = timestamp.getFullYear();
    const month = String(timestamp.getMonth() + 1).padStart(2, '0');
    const day = String(timestamp.getDate()).padStart(2, '0');
    const hours = String(timestamp.getHours()).padStart(2, '0');
    const minutes = String(timestamp.getMinutes()).padStart(2, '0');
    const seconds = String(timestamp.getSeconds()).padStart(2, '0');
    const ms = String(timestamp.getMilliseconds()).padStart(3, '0');
    const suffix = detailed ? '_detailed' : '';
    const filename = `klv_${year}${month}${day}_${hours}${minutes}${seconds}_${ms}${suffix}.json`;
    
    // Create and download JSON file using the current view (detailed or tags)
    const jsonStr = JSON.stringify(klvData, null, 2);
    const blob = new Blob([jsonStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };
  
  const toggleTooltip = detailed ? "Detailed info" : "Tags";
  const ToggleIcon = detailed ? FormatListBulletedIcon : FormatListNumberedIcon;

  return (
    <KlvWindowPortal
      open={open}
      onClose={onClose}
      onReady={handlePortalReady}
    >
      <Box
        sx={{
          bgcolor: "background.default",
          color: "text.primary",
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Toolbar
          variant="dense"
          sx={{
            bgcolor: "background.paper",
            borderBottom: 1,
            borderColor: "divider",
            minHeight: 48,
            px: 2,
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Box sx={{ display: "flex", gap: 1 }}>
            <Tooltip title={isPaused ? "Resume" : "Pause"} placement="right">
              <ToggleButton
                value="pause"
                selected={isPaused}
                onChange={() => setIsPaused((prev) => !prev)}
                color="primary"
                size="small"
                sx={{ p: 1.25, minWidth: 40 }}
                aria-label="Toggle pause"
              >
                {isPaused ? (
                  <PlayCircleOutlineIcon fontSize="small" />
                ) : (
                  <PauseCircleOutlineIcon fontSize="small" />
                )}
              </ToggleButton>
            </Tooltip>

            <Tooltip title="Download packet" placement="right">
              <ToggleButton
                value="download"
                selected={false}
                onClick={handleDownload}
                disabled={!displayKlvPacket?.klv}
                color="primary"
                size="small"
                sx={{ p: 1.25, minWidth: 40 }}
                aria-label="Download KLV packet"
              >
                <DownloadIcon fontSize="small" />
              </ToggleButton>
            </Tooltip>
          </Box>

          <Box sx={{ display: "flex", gap: 1 }}>
            <Tooltip title={toggleTooltip} placement="left">
              <ToggleButton
                value="detailed"
                selected={detailed}
                onChange={() => setDetailed((prev) => !prev)}
                color="primary"
                size="small"
                sx={{ p: 1.25, minWidth: 40 }}
                aria-label="Toggle detailed view"
              >
                <ToggleIcon fontSize="small" />
              </ToggleButton>
            </Tooltip>

            <Tooltip title={timeISO8601 ? "UTC time" : "Local time"} placement="left">
              <ToggleButton
                value="time-format"
                selected={timeISO8601}
                onChange={() => setTimeISO8601((prev) => !prev)}
                color="primary"
                size="small"
                sx={{ p: 1.25, minWidth: 40 }}
                aria-label="Toggle time format"
              >
                <AccessTimeIcon fontSize="small" />
              </ToggleButton>
            </Tooltip>
          </Box>
        </Toolbar>

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: "auto",
            pt: 1,
            px: 2,
            pb: 2,
          }}
        >
          {timestampText ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.25, mb: 1 }}
            >
              {timestampText}
            </Typography>
          ) : null}
          <JsonView
            value={klvData}
            style={jsonViewTheme}
            displayDataTypes={false}
            highlightUpdates={false}
            objectSortKeys={false}
          />
        </Box>
      </Box>
    </KlvWindowPortal>
  );
};

export default KlvWindow;
